// A cobertura do desembolso (D24), o MESMO bloco na área de trabalho e no cartão da Projeção
// Financeira: o percentual, o desembolso projetado pela Ampli e o valor de referência, a
// barra do projetado e a frase de serviços.
//
// Revisão de 2026-10-13 (design D3): o projetado é a soma da parte que segue a origem (o
// físico-financeiro ou a origem gravada) com a parte das regras, e as partes — mais o que
// está sem distribuição — aparecem com valor e percentual (`coverageParts`). A pizza do topo
// (`CoverageDoughnut`) lê as MESMAS partes, então o topo e o cartão não têm como divergir.
//
// `money()` lê `useProjectContext` por dentro (um hook disfarçado): este componente sempre o
// chama, na mesma ordem, e quem o usa o renderiza atrás de `coverage ? ... : null`.
import { money } from "./format";
import { coverageParts, projectedCoverage, projectedServicesLabel } from "./coverage";
import { reais } from "./monthEdit";

// `percentage` é uma FRAÇÃO (0..1), não basis points (`format.js#percentLabel` é em bps).
export const fractionLabel = (value) =>
  value == null
    ? "—"
    : `${(value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

/** "Pelo físico-financeiro 80,0% — R$ 80.000,00": o rótulo, o percentual e o valor de cada parte. */
export const partText = (part) => `${part.label} ${fractionLabel(part.percentage)} — ${reais(part.cents)}`;

/**
 * As partes do valor de referência, em lista. `reais` (puro) no lugar de `money`: o número
 * de partes varia de obra para obra, e `money` chama um hook por dentro.
 */
export const CoverageParts = ({ coverage }) => {
  const parts = coverageParts(coverage);
  if (!parts) return null;
  return (
    <ul className="ampli-disbursement-coverage__parts" data-testid="coverage-parts">
      {parts.map((part) => (
        <li key={part.key} className={`ampli-disbursement-coverage__part is-${part.key}`}>
          <span className="ampli-disbursement-coverage__swatch" aria-hidden="true" />
          {partText(part)}
        </li>
      ))}
    </ul>
  );
};

const ProjectedCoverage = ({ coverage }) => {
  const projected = projectedCoverage(coverage);
  const projectedMoney = money(projected?.projectedCents);
  const referenceMoney = money(projected?.referenceCents);
  if (!projected) return null;
  const width = projected.percentage == null ? 0 : Math.min(100, Math.max(0, projected.percentage * 100));
  return (
    <div className="ampli-disbursement-coverage" data-testid="projected-coverage">
      <div className="ampli-disbursement-coverage__headline">
        <div data-testid="coverage-percentage">
          <span>Cobertura</span>
          <strong>{fractionLabel(projected.percentage)}</strong>
        </div>
        <div data-testid="coverage-projected">
          <span>Desembolso projetado</span>
          <strong>{projectedMoney}</strong>
        </div>
        <div data-testid="coverage-reference">
          <span>Valor de referência</span>
          <strong>{referenceMoney}</strong>
        </div>
      </div>
      {projected.percentage !== null ? (
        <div
          className="ampli-disbursement-coverage__rail"
          role="img"
          aria-label={`${fractionLabel(projected.percentage)} do valor de referência com desembolso projetado`}
        >
          <span className="coverage-segment ruled" style={{ width: `${width}%` }} />
          <span className="coverage-segment at-sight" style={{ width: `${100 - width}%` }} />
        </div>
      ) : (
        <p>Cobertura percentual indisponível.</p>
      )}
      <CoverageParts coverage={coverage} />
      <p className="ampli-disbursement-coverage__legend">{projectedServicesLabel(projected)}</p>
      {coverage?.orphanRules?.length > 0 ? (
        <p className="alert alert-info" role="status">
          {`${coverage.orphanRules.length} regra(s) sem previsto abaixo do alvo — nada foi redistribuído por elas.`}
        </p>
      ) : null}
    </div>
  );
};

export default ProjectedCoverage;
